/**
 * @fileoverview Mock backend for Edit, Archive (drop) and Restore Prospect.
 * @description Registers PATCH /sales/prospects/:id, POST /sales/prospects/:id/archive and POST
 * /sales/prospects/:id/restore on the feature's shared mock adapter, answering in the backend's
 * envelopes with its error codes (Confluence 3.5.11.8, .9 and .11). Writes the shared Tenant store,
 * so an edited or dropped prospect still works with every mock-only row action. Follows the
 * backend's rules: a Super Admin or Sales Head edits an accepted, unarchived prospect in place; a
 * Sales Manager or Executive edits only their own pending submission (and never once it is
 * approved); the bank type and registration number are immutable and the owner is not editable; a
 * lead rating cannot change while the prospect has an open target. Only a Super Admin or Sales Head
 * drops (an accepted, unarchived prospect with no open target, with a mandatory 1-500 character
 * note); only a Super Admin restores. All three check the `If-Match` ETag and send an
 * `Idempotency-Key`. Like the other sales mocks it doesn't check MFA freshness. A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { canReadDetails, invalidCreateBody, toCreateTenantInput } from './prospects-mock.service'
import {
  admissionStateOf,
  approvalRequestIdOf,
  mockCaller,
  openTargetOf,
  prospectEtagOf,
  toBackendRating,
} from './sales-crm-mock-projection'
import { bodyOf, failReply, headerOf, okEnvelope } from './sales-crm-mock-response.utils'
import { salesMockAdapter } from './sales-mock-adapter'
import { archiveTenant, findTenantById, patchTenant, restoreTenant } from './tenants-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type { CreateProspectRequest } from '@/features/sales/types/prospect.types'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { AxiosRequestConfig } from 'axios'

/**
 * @description Reads the prospect a request names and runs the checks the three commands share:
 * visible to the caller (404), an `Idempotency-Key` (428) and the ETag still current (412).
 * @param {AxiosRequestConfig} config - The request config.
 * @param {number} position - Where the prospect id sits in the URL, counted from the end.
 * @returns {{ tenant: Tenant; caller: SalesUserRecord } | MockReply} The prospect and caller, or
 * the error to answer.
 */
function checkCommand(
  config: AxiosRequestConfig,
  position: number,
): { tenant: Tenant; caller: SalesUserRecord } | MockReply {
  const caller = mockCaller()
  const tenant = findTenantById(decodeURIComponent(config.url?.split('/').at(position) ?? ''))
  if (!caller || !tenant || tenant.status !== 'Prospect' || !canReadDetails(tenant, caller)) {
    return failReply(404, '4101', 'Prospect not found')
  }
  if (!headerOf(config, 'Idempotency-Key')) {
    return failReply(428, '0004', 'Idempotency-Key is required')
  }
  return { tenant, caller }
}

/**
 * @description Checks the prospect's ETag the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @param {Tenant} tenant - The prospect.
 * @returns {MockReply | null} The `412` to answer, or null when the ETag is current.
 */
function staleRefusal(config: AxiosRequestConfig, tenant: Tenant): MockReply | null {
  return headerOf(config, 'If-Match') === prospectEtagOf(tenant)
    ? null
    : failReply(412, '4105', 'The prospect changed since it was read')
}

/**
 * @description Whether the caller may edit this prospect: a senior edits an accepted, unarchived one
 * (a pending one is theirs to approve or reject), a Manager or Executive only their own pending
 * submission.
 * @param {Tenant} tenant - The prospect.
 * @param {SalesUserRecord} caller - The signed-in caller.
 * @returns {MockReply | null} The error to answer, or null when they may.
 */
function editRefusal(tenant: Tenant, caller: SalesUserRecord): MockReply | null {
  const isSenior = caller.role === 'Super Admin' || caller.role === 'Sales Head'
  const state = admissionStateOf(tenant)
  if (isSenior) {
    return state === 'ACCEPTED' && !tenant.archived
      ? null
      : failReply(409, '4103', 'The prospect is not accepted and active')
  }
  if (tenant.createdById !== caller.id || state !== 'PENDING_REVIEW') {
    return failReply(403, '2008', 'Forbidden')
  }
  return null
}

/**
 * @description Checks an Edit Prospect body against the backend's immutables and rules.
 * @param {Tenant} tenant - The prospect being edited.
 * @param {CreateProspectRequest} body - The request body.
 * @returns {MockReply | null} The error to answer, or null when the edit is valid.
 */
function bodyRefusal(tenant: Tenant, body: CreateProspectRequest): MockReply | null {
  const invalid = invalidCreateBody(body)
  if (invalid) return failReply(400, '0001', invalid)
  if (body.sales.proposedOwnerUserId) {
    return failReply(400, '0001', 'The owner cannot be edited; use Assign Prospect Owner')
  }
  const input = toCreateTenantInput(body)
  if (input.regNumber !== tenant.regNumber || input.bankType !== tenant.bankType) {
    return failReply(400, '0001', 'The registration number and bank type cannot change')
  }
  if (openTargetOf(tenant.id) && body.sales.leadRating !== toBackendRating(tenant.leadRating)) {
    return failReply(409, '4112', 'The lead rating is locked while the prospect has a target')
  }
  return null
}

/**
 * @description Edits a prospect the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The edit's result, or the backend's error.
 */
function editReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -1)
  if (Array.isArray(checked)) return checked
  const { tenant, caller } = checked
  const refusal =
    editRefusal(tenant, caller) ??
    staleRefusal(config, tenant) ??
    bodyRefusal(tenant, bodyOf(config) as unknown as CreateProspectRequest)
  if (refusal) return refusal

  patchTenant(tenant.id, toCreateTenantInput(bodyOf(config) as unknown as CreateProspectRequest))
  const isPending = admissionStateOf(tenant) === 'PENDING_REVIEW'
  return [
    200,
    okEnvelope({
      prospectId: tenant.id,
      tenantId: tenant.id,
      tenantCode: tenant.id,
      admissionState: admissionStateOf(tenant),
      approvalRequestId: isPending ? approvalRequestIdOf(tenant.id) : null,
      submissionRevision: isPending ? 2 : 1,
    }),
  ]
}

/**
 * @description Drops (archives) a prospect the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The archive state, or the backend's error.
 */
function archiveReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -2)
  if (Array.isArray(checked)) return checked
  const { tenant, caller } = checked
  if (caller.role !== 'Super Admin' && caller.role !== 'Sales Head') {
    return failReply(403, '2008', 'Forbidden')
  }
  const { reasonNote } = bodyOf(config) as { reasonNote?: string }
  const note = typeof reasonNote === 'string' ? reasonNote.trim() : ''
  if (note.length < 1 || note.length > 500) {
    return failReply(400, '0001', 'A reason note of 1-500 characters is required')
  }
  if (admissionStateOf(tenant) !== 'ACCEPTED' || tenant.archived || openTargetOf(tenant.id)) {
    return failReply(409, '4103', 'The prospect cannot be dropped in its current state')
  }
  const stale = staleRefusal(config, tenant)
  if (stale) return stale
  archiveTenant(tenant.id, note)
  tenant.archivedAt = new Date().toISOString()
  tenant.archivedById = caller.id
  return [200, okEnvelope({ prospectId: tenant.id, archiveStatus: 'ARCHIVED' })]
}

/**
 * @description Restores a dropped prospect the way the backend does.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {MockReply} The archive state, or the backend's error.
 */
function restoreReply(config: AxiosRequestConfig): MockReply {
  const checked = checkCommand(config, -2)
  if (Array.isArray(checked)) return checked
  const { tenant, caller } = checked
  if (caller.role !== 'Super Admin') return failReply(403, '2008', 'Forbidden')
  const { reasonNote } = bodyOf(config) as { reasonNote?: string | null }
  if (typeof reasonNote === 'string' && (reasonNote.trim().length < 1 || reasonNote.length > 500)) {
    return failReply(400, '0001', 'The note must be 1-500 characters')
  }
  if (!tenant.archived) return failReply(409, '4103', 'The prospect is not dropped')
  const stale = staleRefusal(config, tenant)
  if (stale) return stale
  restoreTenant(tenant.id)
  tenant.archivedAt = null
  tenant.archivedById = null
  return [200, okEnvelope({ prospectId: tenant.id, archiveStatus: 'ACTIVE' })]
}

if (salesMockAdapter) {
  salesMockAdapter.onPatch(/^\/sales\/prospects\/[^/]+$/).reply(editReply)
  salesMockAdapter.onPost(/^\/sales\/prospects\/[^/]+\/archive$/).reply(archiveReply)
  salesMockAdapter.onPost(/^\/sales\/prospects\/[^/]+\/restore$/).reply(restoreReply)
}
